'use es6';

import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getDimensions } from 'reporting-data/report/configReportGetters';
import { fromMetricKey } from 'reporting-data/v2/dataset/datasetMetrics';
import { SUB_APPS } from 'reporting-data/tsTypes/subAppTypes';
import AsyncSingleReportViewerFullPage from 'reporting-enablement/components/singleReportViewer/AsyncSingleReportViewerFullPage';
import { subAppKeysByTemplateKey } from 'sales-analytics-lib/utils/get-report-config';
import { getQuestionContextOverrides, BASE_QUESTION_CONTEXT_UPDATES } from '../../constants/quickAnswerQuestions';
import { trackEvent } from '../../lib/usageTracker';
import ReportErrorMessage from '../ReportErrorMessage';
import { selectIsReportCollectionTemplateRequestPending, selectReportCollectionTemplatesByKey } from '../../redux/reportCollectionTemplates';
import { getGatesAndScopes } from '../../selectors/gatesSelector';
import { getQueryParamsObject } from '../../utils/queryStrings';
import { getRealmTemplateKeys } from '../../utils/reportingIAUtils';
import GeneratedJourneySrv from './GeneratedJourneySrv';
import { SUITE_SCREEN_NAMES } from '../../constants/screenNames';
import { jsx as _jsx } from "react/jsx-runtime";
const noop = () => {};
const encodeObject = obj => btoa(encodeURIComponent(JSON.stringify(obj)));
const FullPageTemplateReportViewer = ({
  subAppKey,
  height,
  breadcrumbs
}) => {
  const history = useHistory();
  const {
    collectionId
  } = useParams();
  const {
    gates,
    scopes
  } = useSelector(getGatesAndScopes);
  const {
    question,
    tab
  } = getQueryParamsObject(history);
  const collectionTemplatesByKey = useSelector(selectReportCollectionTemplatesByKey);
  const loading = useSelector(selectIsReportCollectionTemplateRequestPending);
  const templateKeys = getRealmTemplateKeys(subAppKey, gates, scopes);
  const templates = loading ? [] : templateKeys.map(templateKey => collectionTemplatesByKey.get(templateKey));
  const template = tab ? templates.find(aTemplate => subAppKeysByTemplateKey.get(aTemplate.get('templateKey')) === tab) : templates[0];
  const isTabbedReport = templates.length > 1;
  useEffect(() => {
    if (template && question) {
      const {
        tab: questionTab,
        grouping: groupingProperty,
        chartMetrics,
        configType,
        chartType
      } = getQuestionContextOverrides(question);
      const dateRangeValue = BASE_QUESTION_CONTEXT_UPDATES.dateRange;
      const queryParams = [`dateRange=${encodeObject(dateRangeValue)}`];
      if (questionTab) {
        queryParams.push(`tab=${questionTab}`);
      }
      if (groupingProperty) {
        const reportDimensions = getDimensions(template).toJS();
        const updatedDimensions = [groupingProperty];
        if (reportDimensions.length === 2 && reportDimensions[1] !== groupingProperty) {
          updatedDimensions.push(reportDimensions[1]);
        }
        queryParams.push(`dimensions=${updatedDimensions.join(',')}`);
      }
      if (chartMetrics) {
        const metrics = chartMetrics.toJS().map(metricKey => {
          const {
            property,
            type
          } = fromMetricKey(metricKey);
          return {
            property,
            metricTypes: [type]
          };
        });
        queryParams.push(`metrics=${encodeObject(metrics)}`);
      }
      if (configType) {
        queryParams.push(`configType=${configType}`);
      }
      if (chartType) {
        queryParams.push(`chartType=${chartType}`);
      }
      const queryString = queryParams.join('&');
      history.push(`${history.location.pathname}?${queryString}`);
    }
  }, [template, loading, question, gates, scopes, subAppKey, history]);
  const sharedSrvProps = useMemo(() => {
    const screenName = SUITE_SCREEN_NAMES[collectionId];
    const subAppTitleKey = `reports.suiteContents.${subAppKey}.title`;
    return {
      title: isTabbedReport && isValidI18nKey(subAppTitleKey) ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: subAppTitleKey
      }) : undefined,
      height,
      onClose: noop,
      hideBackButton: true,
      reportErrorRenderer: ReportErrorMessage,
      track: trackEvent,
      trackingApplicationName: 'reporting',
      trackingScreenName: screenName,
      trackingSubscreenName: subAppKey,
      enableReportTemplateLocalStoragePersistence: true,
      breadcrumbs
    };
  }, [subAppKey, isTabbedReport, height, breadcrumbs, collectionId]);
  if (loading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "centered"
    });
  }
  if (!template) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reports.errors.templateNotFound"
    });
  }
  if ([SUB_APPS.leadFunnel, SUB_APPS.dealJourney].includes(subAppKey)) {
    return /*#__PURE__*/_jsx(GeneratedJourneySrv, Object.assign({
      template: template,
      templates: isTabbedReport ? templates : undefined
    }, sharedSrvProps));
  }
  return /*#__PURE__*/_jsx(AsyncSingleReportViewerFullPage, Object.assign({
    report: template,
    additionalReports: isTabbedReport ? templates : undefined
  }, sharedSrvProps));
};
export default FullPageTemplateReportViewer;